import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Grid,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import PrintIcon from '@mui/icons-material/Print';
import {
  useCreatePurchaseNoteMutation,
  useUpdatePurchaseNoteMutation,
  useGetPurchaseNoteQuery,
  useLazyGetSuppliersQuery,
} from '../../../store/api/purchaseApi';
import { useLazyGetProductsQuery } from '../../../store/api/productsApi';
import { useGetCurrentCompanyQuery } from '../../../store/api/companyApi';

function PurchaseNoteForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isView = !!id;

  const [createPurchaseNote] = useCreatePurchaseNoteMutation();
  const [updatePurchaseNote] = useUpdatePurchaseNoteMutation();
  const [triggerSupplierSearch, { data: supplierSearchData, isFetching: searchingSuppliers }] = useLazyGetSuppliersQuery();
  const [supplierSearchOptions, setSupplierSearchOptions] = useState([]);
  const [triggerProductSearch, { data: productSearchData, isFetching: searchingProducts }] = useLazyGetProductsQuery();
  const { data: company } = useGetCurrentCompanyQuery();
  const currency = company?.currency || 'USD';

  const { data: existingNote, isLoading: loadingNote } = useGetPurchaseNoteQuery(id, { skip: !isView });

  const [formData, setFormData] = useState({
    supplierId: '',
    noteDate: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const [items, setItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');

  useEffect(() => {
    if (supplierSearchData?.data) {
      setSupplierSearchOptions(supplierSearchData.data);
    } else if (Array.isArray(supplierSearchData)) {
      setSupplierSearchOptions(supplierSearchData);
    }
  }, [supplierSearchData]);

  useEffect(() => {
    if (existingNote) {
      setFormData({
        supplierId: existingNote.supplierId || '',
        noteDate: existingNote.noteDate ? existingNote.noteDate.substring(0, 10) : new Date().toISOString().split('T')[0],
        notes: existingNote.notes || '',
      });
      setItems(existingNote.items?.map(it => ({
        productId: it.productId,
        productName: it.productName || it.product?.name,
        quantity: it.quantity,
        unitPrice: it.unitPrice,
      })) || []);
      if (existingNote.supplier && !supplierSearchOptions.find(s => s.id === existingNote.supplier.id)) {
        setSupplierSearchOptions([existingNote.supplier, ...supplierSearchOptions]);
      }
    }
  }, [existingNote]);

  const handleAddItem = (product) => {
    if (!product) return;
    const existingIndex = items.findIndex(it => it.productId === product.id);
    if (existingIndex !== -1) {
      const newItems = [...items];
      newItems[existingIndex].quantity = Number(newItems[existingIndex].quantity) + 1;
      setItems(newItems);
    } else {
      setItems([...items, {
        productId: product.id,
        productName: product.name,
        quantity: 1,
        unitPrice: product.costPrice || 0
      }]);
    }
    setProductSearch('');
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleRemoveItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const calculateSubtotal = () => {
    return items.reduce((sum, it) => sum + (Number(it.quantity) * Number(it.unitPrice)), 0);
  };

  const subtotal = calculateSubtotal();
  // Simplified totals for the note
  const grandTotal = subtotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.supplierId) return alert('Please select a supplier');
    if (items.length === 0) return alert('Please add at least one item');

    const payload = {
      ...formData,
      discountTotal: 0,
      items: items.map(it => ({
        ...it,
        vatRate: 0,
        vatAmount: 0,
        discount: 0,
        total: Number(it.quantity) * Number(it.unitPrice)
      })),
      subtotal,
      vatTotal: 0,
      grandTotal,
    };

    try {
      if (isView) {
        await updatePurchaseNote({ id, ...payload }).unwrap();
      } else {
        await createPurchaseNote(payload).unwrap();
      }
      navigate('/purchases/notes');
    } catch (err) {
      alert(err?.data?.message || 'Failed to save purchase note');
    }
  };

  if (loadingNote) return <Typography sx={{ p: 4 }}>Loading...</Typography>;

  const selectedSupplier = supplierSearchOptions.find(s => s.id === formData.supplierId) || null;

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 4 }}>
        {isView ? 'Edit Purchase Note' : 'New Purchase Note'}
      </Typography>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Autocomplete
                options={supplierSearchOptions}
                loading={searchingSuppliers}
                getOptionLabel={opt => opt.name || ''}
                value={selectedSupplier}
                onChange={(_, val) => setFormData({ ...formData, supplierId: val?.id || '' })}
                onInputChange={(_, val) => {
                  if (val.length >= 2) triggerSupplierSearch({ search: val, limit: 10 });
                }}
                onOpen={() => triggerSupplierSearch({ limit: 10 })}
                renderInput={params => <TextField {...params} label="Supplier" required />}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                type="date"
                label="Date"
                value={formData.noteDate}
                onChange={e => setFormData({ ...formData, noteDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
                <Autocomplete
                  sx={{ flexGrow: 1 }}
                  options={productSearchData?.products || []}
                  loading={searchingProducts}
                  getOptionLabel={opt => `${opt.name} (${opt.sku || 'No SKU'})`}
                  inputValue={productSearch}
                  onInputChange={(_, val) => {
                    setProductSearch(val);
                    if (val.length >= 1) triggerProductSearch({ search: val, limit: 10 });
                  }}
                  onChange={(_, val) => handleAddItem(val)}
                  renderInput={params => <TextField {...params} label="Search Product to Add" />}
                />
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 600 }}>Product</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Quantity</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Unit Price</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {items.map((it, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{it.productName}</TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={it.quantity}
                            onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                            sx={{ width: 80 }}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            type="number"
                            size="small"
                            value={it.unitPrice}
                            onChange={e => handleItemChange(idx, 'unitPrice', e.target.value)}
                            sx={{ width: 100 }}
                          />
                        </TableCell>
                        <TableCell>{(Number(it.quantity) * Number(it.unitPrice)).toFixed(2)}</TableCell>
                        <TableCell>
                          <IconButton color="error" onClick={() => handleRemoveItem(idx)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                    {items.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          No items added yet
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            {/* Totals Section */}
            <Grid size={{ xs: 12, md: 7 }}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Notes"
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 5 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1">Subtotal:</Typography>
                  <Typography variant="body1" sx={{ fontWeight: 600 }}>{currency} {subtotal.toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1, pt: 1, borderTop: '1px solid #eee' }}>
                  <Typography variant="h6">Grand Total:</Typography>
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 700 }}>
                    {currency} {grandTotal.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid size={{ xs: 12 }} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 2 }}>
              <Button variant="outlined" onClick={() => navigate('/purchases/notes')}>Cancel</Button>
              {isView && (
                <Button
                  variant="outlined"
                  color="secondary"
                  startIcon={<PrintIcon />}
                  onClick={() => navigate(`/purchases/notes/${id}/print`)}
                >
                  Print PDF
                </Button>
              )}
              <Button variant="contained" type="submit" size="large">
                {isView ? 'Update Note' : 'Save Purchase Note'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
}

export default PurchaseNoteForm;
